import { GTMInitiative } from '../types/gtm';
import { Solution } from '../types/solution';
import { BlogPost, SuccessStory } from '../types/content';
import { AWSService } from '../types/common';

// Mock AWS Services data
export const mockAWSServices: AWSService[] = [
  {
    name: 'Amazon ECS',
    category: 'Containers',
    description: 'Fully managed container orchestration service'
  },
  {
    name: 'AWS Fargate',
    category: 'Containers',
    description: 'Serverless compute engine for containers'
  },
  {
    name: 'AWS Lambda',
    category: 'Compute',
    description: 'Run code without thinking about servers'
  },
  {
    name: 'Amazon S3',
    category: 'Storage',
    description: 'Object storage built to retrieve any amount of data'
  },
  {
    name: 'Amazon RDS',
    category: 'Database',
    description: 'Managed relational database service'
  },
  {
    name: 'Amazon API Gateway',
    category: 'Networking',
    description: 'Create, publish, maintain, monitor, and secure APIs'
  },
  {
    name: 'Amazon CloudFront',
    category: 'Networking',
    description: 'Fast content delivery network (CDN) service'
  },
  {
    name: 'Amazon SageMaker',
    category: 'Machine Learning',
    description: 'Build, train, and deploy machine learning models'
  }
];

// Mock GTM Initiatives data
export const mockGTMInitiatives: GTMInitiative[] = [
  {
    id: 'gtm-1',
    title: 'Modernise Your Contact Centre with HCLTech',
    tagline: 'Transform customer experience with AI-powered contact center solutions',
    heroImage: '/images/contact-center-hero.jpg',
    description: 'Revolutionize your customer service operations with our comprehensive contact center modernization solution. Leverage AI, machine learning, and cloud-native technologies to deliver exceptional customer experiences while reducing operational costs.',
    valueAdd: 'Reduce operational costs by 40%, improve customer satisfaction scores by 35%, and enable 24/7 intelligent customer support with multilingual capabilities.',
    roiDescription: 'Typical ROI of 250% within 18 months through reduced agent costs, improved first-call resolution rates, and enhanced customer retention.',
    relatedSolutions: ['sol-1', 'sol-2'],
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-02-01')
  },
  {
    id: 'gtm-2',
    title: 'Build Your Generative AI Practice on AWS',
    tagline: 'Accelerate innovation with enterprise-ready generative AI solutions',
    heroImage: '/images/genai-hero.jpg',
    description: 'Establish a robust generative AI practice with our comprehensive framework built on AWS. From strategy to implementation, we help organizations harness the power of large language models and generative AI for business transformation.',
    valueAdd: 'Accelerate time-to-market for AI initiatives by 60%, reduce development costs by 45%, and ensure enterprise-grade security and compliance.',
    roiDescription: 'Organizations see an average ROI of 300% within 24 months through improved productivity, automated processes, and new revenue streams.',
    relatedSolutions: ['sol-3', 'sol-4'],
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-02-05')
  },
  {
    id: 'gtm-3',
    title: 'Accelerate Mainframe Modernization',
    tagline: 'Seamlessly migrate legacy systems to cloud-native architectures',
    heroImage: '/images/mainframe-hero.jpg',
    description: 'Transform your legacy mainframe systems with our proven modernization approach. Migrate to AWS cloud infrastructure while maintaining business continuity and ensuring zero data loss.',
    valueAdd: 'Reduce infrastructure costs by 50%, improve system performance by 3x, and enable rapid deployment of new features and services.',
    roiDescription: 'Achieve ROI of 200% within 36 months through reduced maintenance costs, improved agility, and enhanced scalability.',
    relatedSolutions: ['sol-5', 'sol-6'],
    createdAt: new Date('2024-01-25'),
    updatedAt: new Date('2024-02-10')
  }
];

// Mock Solutions data
export const mockSolutions: Solution[] = [
  {
    id: 'sol-1',
    title: 'AI-Powered Customer Service Platform',
    summary: 'Intelligent contact center solution with natural language processing and automated routing.',
    industry: 'Financial Services',
    valueProposition: 'Reduce customer service costs by 40% while improving satisfaction scores through AI-driven interactions and intelligent routing.',
    velocityEstimation: '12-16 weeks implementation',
    arrEstimation: '$2.5M - $5M ARR potential',
    demoVideo: {
      url: '/videos/ai-customer-service-demo.mp4',
      altText: 'AI Customer Service Platform Demo',
      type: 'video'
    },
    architectureDiagram: {
      url: '/images/ai-customer-service-architecture.png',
      altText: 'AI Customer Service Architecture Diagram',
      type: 'image'
    },
    awsServices: [mockAWSServices[2], mockAWSServices[5], mockAWSServices[7]], // Lambda, API Gateway, SageMaker
    references: [
      {
        clientName: 'Global Bank Corp',
        quote: 'HCLTech\'s AI-powered solution reduced our call center costs by 45% while improving customer satisfaction scores by 30%.',
        industry: 'Financial Services',
        caseStudyUrl: '/case-studies/global-bank-ai-contact-center'
      }
    ],
    relatedGTMInitiatives: ['gtm-1'],
    tags: ['AI', 'Customer Service', 'NLP', 'Automation'],
    status: 'active',
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-02-01')
  },
  {
    id: 'sol-2',
    title: 'Omnichannel Communication Hub',
    summary: 'Unified platform integrating voice, chat, email, and social media customer interactions.',
    industry: 'Retail',
    valueProposition: 'Provide seamless customer experience across all channels with 360-degree customer view and intelligent routing.',
    velocityEstimation: '8-12 weeks implementation',
    arrEstimation: '$1.5M - $3M ARR potential',
    demoVideo: {
      url: '/videos/omnichannel-demo.mp4',
      altText: 'Omnichannel Communication Hub Demo',
      type: 'video'
    },
    awsServices: [mockAWSServices[0], mockAWSServices[1], mockAWSServices[3]], // ECS, Fargate, S3
    references: [
      {
        clientName: 'RetailMax Inc',
        quote: 'The omnichannel solution increased our customer engagement by 50% and reduced response times by 60%.',
        industry: 'Retail'
      }
    ],
    relatedGTMInitiatives: ['gtm-1'],
    tags: ['Omnichannel', 'Customer Experience', 'Integration'],
    status: 'active',
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-28')
  },
  {
    id: 'sol-3',
    title: 'Enterprise GenAI Content Generator',
    summary: 'Secure, compliant generative AI platform for enterprise content creation and automation.',
    industry: 'Technology',
    valueProposition: 'Accelerate content creation by 10x while maintaining brand consistency and regulatory compliance.',
    velocityEstimation: '16-20 weeks implementation',
    arrEstimation: '$3M - $8M ARR potential',
    demoVideo: {
      url: '/videos/genai-content-demo.mp4',
      altText: 'Enterprise GenAI Content Generator Demo',
      type: 'video'
    },
    awsServices: [mockAWSServices[7], mockAWSServices[2], mockAWSServices[3]], // SageMaker, Lambda, S3
    references: [
      {
        clientName: 'TechCorp Solutions',
        quote: 'HCLTech\'s GenAI platform transformed our content operations, reducing creation time by 80% while improving quality.',
        industry: 'Technology',
        caseStudyUrl: '/case-studies/techcorp-genai-transformation'
      }
    ],
    relatedGTMInitiatives: ['gtm-2'],
    tags: ['Generative AI', 'Content Creation', 'Automation', 'Enterprise'],
    status: 'active',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-02-05')
  }
];

// Mock Blog Posts data
export const mockBlogPosts: BlogPost[] = [
  {
    id: 'blog-1',
    title: 'The Future of AI in Customer Service: Trends and Predictions for 2024',
    excerpt: 'Explore how artificial intelligence is revolutionizing customer service operations and what businesses can expect in the coming year.',
    content: 'Full article content about AI in customer service...',
    featuredImage: {
      url: '/images/ai-customer-service-blog.jpg',
      altText: 'AI Customer Service Future Trends',
      type: 'image'
    },
    author: {
      id: 'author-1',
      name: 'Sarah Johnson',
      title: 'AI Solutions Architect',
      avatar: {
        url: '/images/authors/sarah-johnson.jpg',
        altText: 'Sarah Johnson Profile',
        type: 'image'
      }
    },
    publishDate: new Date('2024-01-15'),
    readTime: 8,
    tags: ['AI', 'Customer Service', 'Technology Trends'],
    relatedSolutions: ['sol-1'],
    status: 'published'
  },
  {
    id: 'blog-2',
    title: 'Mainframe to Cloud: A Strategic Approach to Legacy System Modernization',
    excerpt: 'Learn the key strategies and best practices for successfully migrating legacy mainframe systems to modern cloud architectures.',
    content: 'Full article content about mainframe modernization...',
    featuredImage: {
      url: '/images/mainframe-modernization-blog.jpg',
      altText: 'Mainframe to Cloud Migration',
      type: 'image'
    },
    author: {
      id: 'author-2',
      name: 'Michael Chen',
      title: 'Cloud Migration Specialist',
      avatar: {
        url: '/images/authors/michael-chen.jpg',
        altText: 'Michael Chen Profile',
        type: 'image'
      }
    },
    publishDate: new Date('2024-01-22'),
    readTime: 12,
    tags: ['Cloud Migration', 'Mainframe', 'Modernization'],
    relatedSolutions: ['sol-5', 'sol-6'],
    status: 'published'
  },
  {
    id: 'blog-3',
    title: 'Building Responsible AI: Ethics and Governance in Enterprise GenAI',
    excerpt: 'Discover how organizations can implement ethical AI practices and governance frameworks for generative AI initiatives.',
    content: 'Full article content about responsible AI...',
    featuredImage: {
      url: '/images/responsible-ai-blog.jpg',
      altText: 'Responsible AI Ethics',
      type: 'image'
    },
    author: {
      id: 'author-3',
      name: 'Dr. Priya Patel',
      title: 'AI Ethics Researcher',
      avatar: {
        url: '/images/authors/priya-patel.jpg',
        altText: 'Dr. Priya Patel Profile',
        type: 'image'
      }
    },
    publishDate: new Date('2024-02-01'),
    readTime: 10,
    tags: ['AI Ethics', 'Governance', 'Responsible AI'],
    relatedSolutions: ['sol-3', 'sol-4'],
    status: 'published'
  }
];

// Mock Success Stories data
export const mockSuccessStories: SuccessStory[] = [
  {
    id: 'story-1',
    title: 'Global Bank Transforms Customer Experience with AI-Powered Contact Center',
    excerpt: 'How a leading financial institution reduced operational costs by 45% while improving customer satisfaction through intelligent automation.',
    content: 'Full success story content...',
    featuredImage: {
      url: '/images/success-stories/global-bank-transformation.jpg',
      altText: 'Global Bank AI Transformation Success Story',
      type: 'image'
    },
    clientName: 'Global Bank Corp',
    industry: 'Financial Services',
    publishDate: new Date('2024-01-10'),
    readTime: 6,
    tags: ['Financial Services', 'AI', 'Customer Experience'],
    relatedSolutions: ['sol-1'],
    status: 'published'
  },
  {
    id: 'story-2',
    title: 'RetailMax Achieves 50% Increase in Customer Engagement with Omnichannel Solution',
    excerpt: 'Discover how a major retailer unified their customer communications across all channels to drive engagement and sales.',
    content: 'Full success story content...',
    featuredImage: {
      url: '/images/success-stories/retailmax-omnichannel.jpg',
      altText: 'RetailMax Omnichannel Success Story',
      type: 'image'
    },
    clientName: 'RetailMax Inc',
    industry: 'Retail',
    publishDate: new Date('2024-01-18'),
    readTime: 5,
    tags: ['Retail', 'Omnichannel', 'Customer Engagement'],
    relatedSolutions: ['sol-2'],
    status: 'published'
  },
  {
    id: 'story-3',
    title: 'TechCorp Revolutionizes Content Operations with Enterprise GenAI Platform',
    excerpt: 'Learn how a technology company reduced content creation time by 80% while maintaining quality and brand consistency.',
    content: 'Full success story content...',
    featuredImage: {
      url: '/images/success-stories/techcorp-genai.jpg',
      altText: 'TechCorp GenAI Success Story',
      type: 'image'
    },
    clientName: 'TechCorp Solutions',
    industry: 'Technology',
    publishDate: new Date('2024-01-25'),
    readTime: 7,
    tags: ['Technology', 'GenAI', 'Content Creation'],
    relatedSolutions: ['sol-3'],
    status: 'published'
  }
];

// Additional mock solutions
export const additionalMockSolutions: Solution[] = [
  {
    id: 'sol-4',
    title: 'Intelligent Document Processing Platform',
    summary: 'AI-powered document analysis and processing solution for enterprise workflows.',
    industry: 'Healthcare',
    valueProposition: 'Automate document processing workflows, reduce manual effort by 85%, and improve accuracy through AI-powered extraction.',
    velocityEstimation: '14-18 weeks implementation',
    arrEstimation: '$2M - $6M ARR potential',
    demoVideo: {
      url: '/videos/document-processing-demo.mp4',
      altText: 'Intelligent Document Processing Demo',
      type: 'video'
    },
    awsServices: [mockAWSServices[7], mockAWSServices[2], mockAWSServices[4]], // SageMaker, Lambda, RDS
    references: [
      {
        clientName: 'HealthSystem Plus',
        quote: 'The document processing platform reduced our administrative overhead by 70% and improved patient data accuracy.',
        industry: 'Healthcare'
      }
    ],
    relatedGTMInitiatives: ['gtm-2'],
    tags: ['Document Processing', 'AI', 'Healthcare', 'Automation'],
    status: 'active',
    createdAt: new Date('2024-01-18'),
    updatedAt: new Date('2024-02-03')
  },
  {
    id: 'sol-5',
    title: 'Legacy System Migration Accelerator',
    summary: 'Automated tools and frameworks for migrating mainframe applications to AWS cloud.',
    industry: 'Financial Services',
    valueProposition: 'Reduce migration time by 60%, minimize business disruption, and ensure data integrity throughout the process.',
    velocityEstimation: '24-36 weeks implementation',
    arrEstimation: '$5M - $15M ARR potential',
    demoVideo: {
      url: '/videos/migration-accelerator-demo.mp4',
      altText: 'Legacy System Migration Demo',
      type: 'video'
    },
    architectureDiagram: {
      url: '/images/migration-architecture.png',
      altText: 'Migration Architecture Diagram',
      type: 'image'
    },
    awsServices: [mockAWSServices[0], mockAWSServices[4], mockAWSServices[3]], // ECS, RDS, S3
    references: [
      {
        clientName: 'Heritage Financial',
        quote: 'HCLTech\'s migration accelerator helped us move our core banking system to AWS with zero downtime.',
        industry: 'Financial Services',
        caseStudyUrl: '/case-studies/heritage-financial-migration'
      }
    ],
    relatedGTMInitiatives: ['gtm-3'],
    tags: ['Migration', 'Mainframe', 'Cloud', 'Financial Services'],
    status: 'active',
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-02-08')
  },
  {
    id: 'sol-6',
    title: 'Cloud-Native Application Modernization',
    summary: 'Transform monolithic applications into microservices architecture on AWS.',
    industry: 'Manufacturing',
    valueProposition: 'Improve application scalability by 5x, reduce infrastructure costs by 40%, and enable rapid feature deployment.',
    velocityEstimation: '20-28 weeks implementation',
    arrEstimation: '$3M - $10M ARR potential',
    awsServices: [mockAWSServices[0], mockAWSServices[1], mockAWSServices[5]], // ECS, Fargate, API Gateway
    references: [
      {
        clientName: 'Industrial Solutions Corp',
        quote: 'The modernization project transformed our legacy systems into a scalable, cloud-native platform.',
        industry: 'Manufacturing'
      }
    ],
    relatedGTMInitiatives: ['gtm-3'],
    tags: ['Modernization', 'Microservices', 'Cloud-Native', 'Manufacturing'],
    status: 'active',
    createdAt: new Date('2024-01-22'),
    updatedAt: new Date('2024-02-06')
  }
];

// Combine all solutions
export const allMockSolutions = [...mockSolutions, ...additionalMockSolutions];